<script setup lang="ts">
/* =============================================================
   BOOKING — DETAIL
   =============================================================
   One booking, seen from whichever side the reader is on. The
   same row is visible to the booker and to the berth owner, and
   each gets only their own actions: the owner confirms or
   rejects a request, the booker cancels before arrival. The
   database functions enforce the same split; the page only
   avoids offering a button that would be refused.

   No edit and no delete. A booking's times and price are a
   record of what was agreed; changing them is a new booking.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import BookingStatusCard from '@/components/bookings/BookingStatusCard.vue'
import { routes, to } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { cancelBooking, getBooking, respondToBooking } from '@/lib/bookings'
import { getBerth } from '@/lib/berths'
import { listRates } from '@/lib/rates'
import { useAmenities } from '@/lib/amenities'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import {
  bookingModeLabel,
  bookingState,
  formatDateTime,
  formatDuration,
  formatEur,
  rateConditionText,
  ratePriceText,
} from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import type { BerthRate, Berth, Booking } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { user } = useAuth()
const { amenityLabel } = useAmenities()

const booking = ref<Booking | null>(null)
/** The full berth, for the owner's name and the usual response time. */
const berth = ref<Berth | null>(null)
const rate = ref<BerthRate | null>(null)
const loading = ref(true)
const loadError = ref('')
const actionError = ref('')

const id = computed(() => Number(route.params.id))
useDocumentTitle(() => booking.value?.reference ?? 'Booking')

/** Set by the booking flow on the redirect straight after booking. */
const justCreated = computed(() => route.query.created === '1')

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    const b = await getBooking(id.value)
    booking.value = b
    if (!b) return
    // The rule that set the price, to say why it is what it is. Looked up on
    // the card rather than embedded: it may since have been deleted, and then
    // rate_id is null and nothing is shown. Neither extra read is worth
    // failing the page over.
    const [rates, fullBerth] = await Promise.all([
      b.rate_id ? listRates(b.berth_id).catch(() => []) : Promise.resolve([]),
      getBerth(b.berth_id).catch(() => null),
    ])
    rate.value = rates.find((r) => r.id === b.rate_id) ?? null
    berth.value = fullBerth
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load the booking.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

const state = computed(() => (booking.value ? bookingState(booking.value) : null))

/* --- which side the reader is on ----------------------------------------- */

const companyId = computed(() => user.value?.companyId)
/** The berth is ours: we decide on requests. */
const isOwner = computed(() => booking.value?.berth?.company_id === companyId.value)
/** We made the booking: we can cancel it. */
const isBooker = computed(() => booking.value?.company_id === companyId.value)

const canRespond = computed(() => isOwner.value && booking.value?.status === 'requested')
const canCancel = computed(() => {
  const b = booking.value
  if (!b || !isBooker.value) return false
  return (b.status === 'requested' || b.status === 'confirmed') && new Date(b.arrival).getTime() > Date.now()
})

const ownerName = computed(() => berth.value?.company?.name ?? null)
const responseHours = computed(() => berth.value?.response_hours ?? null)

const createdMessage = computed(() => {
  const b = booking.value
  if (!b || !justCreated.value) return null
  if (b.status === 'confirmed') return { title: 'Booked.', text: 'Your booking is confirmed.' }
  if (b.status === 'requested') {
    const when = responseHours.value ? ` — usually within ${responseHours.value} hours` : ''
    return { title: 'Request sent.', text: `The berth owner confirms or rejects it${when}.` }
  }
  return null
})

/* --- what the status means, in a sentence -------------------------------- */

const explanation = computed(() => {
  const b = booking.value
  if (!b || !state.value) return ''
  const owner = ownerName.value ?? 'the berth owner'
  switch (state.value) {
    case 'requested':
      if (isOwner.value) {
        return `${b.company?.name ?? 'A ship operator'} asked for this slot. It is held until you confirm or reject it.`
      }
      return responseHours.value
        ? `Waiting for ${owner} to confirm — usually within ${responseHours.value} hours. The slot is held for you meanwhile.`
        : `Waiting for ${owner} to confirm. The slot is held for you meanwhile.`
    case 'confirmed':
      return b.berth?.booking_mode === 'manual'
        ? 'Confirmed by the berth owner. The berth is reserved for this stay.'
        : 'Confirmed instantly. The berth is reserved for this stay.'
    case 'completed':
      return 'The stay has ended.'
    case 'rejected':
      return isOwner.value
        ? 'You rejected this request. The slot was released.'
        : `${owner} could not take this request. Search again for another berth or time.`
    case 'cancelled':
      return 'Cancelled by the booker before arrival. No fee was charged.'
  }
  return ''
})

/* --- details --------------------------------------------------------------- */

const berthLink = computed(() => {
  const b = booking.value
  if (!b?.berth) return undefined
  return isOwner.value ? to.berth(b.berth.id) : router.resolve(to.berthProfile(b.berth.id)).fullPath
})

const rows = computed<DetailRow[]>(() => {
  const b = booking.value
  if (!b) return []
  const list: DetailRow[] = [
    b.berth
      ? { label: 'Berth', value: `${b.berth.name}, ${b.berth.city}`, to: berthLink.value }
      : { label: 'Berth', empty: true },
  ]
  // Each side already knows who it is; show the other one.
  if (!isOwner.value) {
    list.push(ownerName.value ? { label: 'Berth owner', value: ownerName.value } : { label: 'Berth owner', empty: true })
  }
  // The embed can be null when the reader may not see the vessel. A dash
  // would read as "no vessel", so the row is left out instead.
  if (b.vessel || isBooker.value) list.push(
    b.vessel
      ? {
          label: 'Vessel',
          value: `${b.vessel.name} · ${b.vessel.length_m} m`,
          to: isBooker.value ? to.vessel(b.vessel.id) : undefined,
        }
      : { label: 'Vessel', empty: true },
  )
  if (!isBooker.value) {
    list.push(b.company ? { label: 'Booked by', value: b.company.name } : { label: 'Booked by', empty: true })
  }
  list.push(
    { label: 'Arrival', value: formatDateTime(b.arrival) },
    { label: 'Departure', value: formatDateTime(b.departure) },
    { label: 'Length of stay', value: formatDuration(b.arrival, b.departure) },
    b.requested_amenities.length > 0
      ? { label: 'Requested amenities', tags: b.requested_amenities.map(amenityLabel) }
      : { label: 'Requested amenities', value: 'None', empty: false },
    b.berth
      ? { label: 'Booking mode', value: bookingModeLabel(b.berth.booking_mode) }
      : { label: 'Booking mode', empty: true },
    { label: 'Requested on', value: formatDateTime(b.created_at) },
  )
  if (b.decided_at) {
    list.push({ label: b.status === 'rejected' ? 'Rejected on' : 'Confirmed on', value: formatDateTime(b.decided_at) })
  }
  if (b.cancelled_at) list.push({ label: 'Cancelled on', value: formatDateTime(b.cancelled_at) })
  return list
})

/** A confirmation only exists for a stay that was actually agreed. */
const hasConfirmation = computed(() => state.value === 'confirmed' || state.value === 'completed')

/* --- actions --------------------------------------------------------------- */

const acting = ref<'' | 'confirm' | 'reject'>('')

async function respond(accept: boolean): Promise<void> {
  const b = booking.value
  if (!b || acting.value) return
  acting.value = accept ? 'confirm' : 'reject'
  actionError.value = ''
  try {
    await respondToBooking(b.id, accept)
    await load()
  } catch (e) {
    actionError.value = errorMessage(e, 'Could not update the booking.')
  } finally {
    acting.value = ''
  }
}

const rejecting = ref(false)
const rejectDialog = useTemplateRef<InstanceType<typeof ConfirmDialog>>('rejectDialog')

async function onRejectConfirmed(): Promise<void> {
  const b = booking.value
  if (!b) return
  try {
    await respondToBooking(b.id, false)
    rejecting.value = false
    await load()
  } catch (e) {
    rejectDialog.value?.fail(errorMessage(e, 'Could not reject the request.'))
  }
}

const cancelling = ref(false)
const cancelDialog = useTemplateRef<InstanceType<typeof ConfirmDialog>>('cancelDialog')

async function onCancelConfirmed(): Promise<void> {
  const b = booking.value
  if (!b) return
  try {
    await cancelBooking(b.id)
    cancelling.value = false
    await load()
  } catch (e) {
    cancelDialog.value?.fail(errorMessage(e, 'Could not cancel the booking.'))
  }
}
</script>

<template>
  <p v-if="loading && !booking" class="page-status">Loading…</p>

  <template v-else-if="booking && state">
    <PageHeader
      :title="`Booking ${booking.reference}`"
      :description="booking.berth ? `${booking.berth.name}, ${booking.berth.city}` : undefined"
    >
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.bookings" class="back-link">
          All bookings
        </Button>
        <Button
          v-if="hasConfirmation"
          variant="secondary"
          icon="bookings"
          :to="to.bookingConfirmation(booking.id)"
        >
          Confirmation
        </Button>
      </template>
    </PageHeader>

    <Alert v-if="createdMessage" tone="success" :title="createdMessage.title" class="page-alert">
      {{ createdMessage.text }}
    </Alert>

    <Alert v-if="actionError" tone="danger" class="page-alert">{{ actionError }}</Alert>

    <div class="layout">
      <div class="main">
        <BookingStatusCard
          :state="state"
          :explanation="explanation"
          :label="state === 'requested' && isOwner ? 'Awaiting your answer' : undefined"
        >
          <template v-if="canRespond || canCancel" #actions>
            <template v-if="canRespond">
              <Button
                variant="secondary"
                icon="close"
                :disabled="acting !== ''"
                @click="rejecting = true"
              >
                Reject
              </Button>
              <Button
                icon="check"
                :loading="acting === 'confirm'"
                :disabled="acting !== ''"
                @click="respond(true)"
              >
                Confirm booking
              </Button>
            </template>
            <Button v-if="canCancel" variant="danger" icon="trash" @click="cancelling = true">
              Cancel booking
            </Button>
          </template>
        </BookingStatusCard>

        <Panel title="Stay">
          <DetailList :rows="rows" />
        </Panel>
      </div>

      <Panel title="Price" class="price">
        <p class="amount">{{ formatEur(booking.price_eur) }}</p>
        <p class="vat">Net of VAT</p>
        <div v-if="rate" class="rule">
          <p class="ruleLabel">Set by the berth's rate card</p>
          <p class="ruleText">{{ rateConditionText(rate) }}</p>
          <p class="ruleText strong">{{ ratePriceText(rate) }}</p>
        </div>
        <p v-else class="ruleLabel">Priced at the time of booking.</p>
        <Badge v-if="state === 'cancelled'" class="noFee">No cancellation fee</Badge>
      </Panel>
    </div>

    <ConfirmDialog
      ref="rejectDialog"
      :open="rejecting"
      title="Reject request"
      :message="`${booking.company?.name ?? 'The operator'} will be told you cannot take ${booking.vessel?.name ?? 'the vessel'} for this stay, and the slot is released.`"
      confirm-label="Reject request"
      @close="rejecting = false"
      @confirmed="onRejectConfirmed"
    />

    <ConfirmDialog
      ref="cancelDialog"
      :open="cancelling"
      title="Cancel booking"
      :message="`${booking.reference} will be cancelled and the slot freed. No cancellation fee is charged in the demo.`"
      confirm-label="Cancel booking"
      @close="cancelling = false"
      @confirmed="onCancelConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState v-else icon="bookings" title="Booking not found" description="The link may be wrong.">
      <template #action>
        <Button :to="routes.bookings">All bookings</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--space-sm);
  align-items: start;
}

@media (max-width: 62rem) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.main {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  min-width: 0;
}

.amount {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--color-heading);
  font-variant-numeric: tabular-nums;
}

.vat {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.rule {
  margin-top: var(--space-sm);
  padding-top: var(--space-sm);
  border-top: var(--border-width) solid var(--color-border);
}

.ruleLabel {
  margin-top: var(--space-sm);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.rule .ruleLabel {
  margin-top: 0;
  margin-bottom: var(--space-3xs);
}

.ruleText {
  font-size: var(--text-sm);
  color: var(--color-text);
}

.strong {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.noFee {
  margin-top: var(--space-sm);
}
</style>
